// src/components/TermsOfService.jsx
// Full-screen Terms of Service page. Opened via legalPageOpen state in App.jsx.
// Sticky top bar with ← BACK button; scrollable content.
const BG2   = "#111111";
const BDR2  = "#242424";
const TEXT  = "#f0f0f0";
const MUTED = "#888888";
const FAINT = "#555555";
const MONO  = "var(--font-mono, 'IBM Plex Mono', 'Courier New', monospace)";
const DISP  = "var(--font-display, 'Playfair Display', Georgia, serif)";
const GREEN = "var(--accent, #3ddc84)";

function Section({ number, title, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 16, marginBottom: 12 }}>
        <span style={{ fontFamily: MONO, fontSize: 9, color: GREEN, letterSpacing: ".15em", flexShrink: 0 }}>
          {String(number).padStart(2, "0")}
        </span>
        <h2 style={{ fontFamily: DISP, fontSize: 16, letterSpacing: ".08em", color: TEXT, margin: 0 }}>
          {title.toUpperCase()}
        </h2>
      </div>
      <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, lineHeight: 1.9, paddingLeft: 36 }}>
        {children}
      </div>
    </div>
  );
}

export default function TermsOfService({ onBack }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg, #0a0a0a)", fontFamily: MONO, color: TEXT }}>

      {/* Sticky top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: "rgba(10,10,10,0.96)", backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${BDR2}`,
        height: 52,
        display: "flex", alignItems: "center", padding: "0 32px", gap: 12,
      }}>
        <span style={{ fontFamily: DISP, fontSize: 15, letterSpacing: ".1em", color: TEXT }}>
          NUVPORT<span style={{ color: GREEN }}> OS</span>
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onBack}
          style={{
            background: "none", border: `1px solid ${BDR2}`,
            color: MUTED, fontFamily: MONO, fontSize: 10,
            letterSpacing: ".08em", padding: "6px 14px", cursor: "pointer",
            transition: "color .15s, border-color .15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = TEXT; e.currentTarget.style.borderColor = MUTED; }}
          onMouseLeave={e => { e.currentTarget.style.color = MUTED; e.currentTarget.style.borderColor = BDR2; }}
        >
          ← BACK
        </button>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "60px 32px 100px" }}>

        {/* Header */}
        <div style={{ marginBottom: 60 }}>
          <div style={{ fontSize: 9, letterSpacing: ".2em", color: GREEN, marginBottom: 12 }}>
            // LEGAL
          </div>
          <h1 style={{ fontFamily: DISP, fontSize: "clamp(36px,5vw,60px)", letterSpacing: ".04em", color: TEXT, margin: "0 0 16px" }}>
            TERMS OF SERVICE
          </h1>
          <div style={{ fontSize: 10, color: FAINT, letterSpacing: ".08em" }}>
            Effective date: March 2026 · nuvport.com
          </div>
        </div>

        <Section number={1} title="Acceptance of Terms">
          <p>By creating an account or accessing Nuvport OS ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.</p>
          <p style={{ marginTop: 12 }}>These Terms apply to all users of the Service, including visitors, registered users, and paying subscribers.</p>
        </Section>

        <Section number={2} title="Description of Service">
          <p>Nuvport OS is a web-based portfolio analytics platform ("the Service") operated by nuvport.com. The Service provides tools for building, analysing, and optimising equity portfolios, including live market data, financial modelling, and AI-powered analysis features.</p>
          <p style={{ marginTop: 12 }}>The Service is provided on an "as is" and "as available" basis. We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time with or without notice.</p>
        </Section>

        <Section number={3} title="User Accounts">
          <p>You must create an account to access the Service. You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</p>
          <p style={{ marginTop: 12 }}>You agree to provide accurate and complete information when creating your account and to update that information as necessary. You must be at least 13 years of age to use the Service.</p>
          <p style={{ marginTop: 12 }}>We reserve the right to suspend or terminate your account if any information provided is found to be inaccurate, incomplete, or in violation of these Terms.</p>
        </Section>

        <Section number={4} title="Acceptable Use">
          <p>You agree not to:</p>
          <ul style={{ marginTop: 8, paddingLeft: 20, lineHeight: 2 }}>
            <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations</li>
            <li>Attempt to gain unauthorised access to the Service, its servers, or any connected systems</li>
            <li>Reverse-engineer, decompile, or disassemble any part of the Service</li>
            <li>Use automated means (bots, scrapers, crawlers) to access the Service without our express written consent</li>
            <li>Transmit viruses, malware, or any code that may damage or interfere with the Service</li>
            <li>Use the Service in any way that could impair its performance or availability to other users</li>
            <li>Resell or redistribute the Service or its data without our express written consent</li>
          </ul>
        </Section>

        <Section number={5} title="Intellectual Property">
          <p>All content, features, and functionality of the Service, including but not limited to text, graphics, logos, icons, and software, are the exclusive property of nuvport.com and are protected by applicable intellectual property laws.</p>
          <p style={{ marginTop: 12 }}>You retain ownership of any portfolio data, custom notes, or other content you submit to the Service. By submitting such content, you grant us a limited, non-exclusive, royalty-free licence to store and process it solely for the purpose of providing the Service to you.</p>
        </Section>

        <Section number={6} title="Financial Data Disclaimer">
          <p>Nuvport OS displays financial data including live market quotes, historical prices, and analytical calculations. This data is provided for informational and educational purposes only.</p>
          <p style={{ marginTop: 12 }}>THE SERVICE DOES NOT PROVIDE INVESTMENT ADVICE. Nothing on the Service constitutes a recommendation to buy, sell, or hold any security. Market data may be delayed or inaccurate. Past performance is not indicative of future results.</p>
          <p style={{ marginTop: 12 }}>Always consult a qualified financial advisor before making investment decisions. You assume all risk associated with your investment decisions.</p>
        </Section>

        <Section number={7} title="Disclaimers of Warranties">
          <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.</p>
          <p style={{ marginTop: 12 }}>We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components. We do not warrant the accuracy, completeness, or timeliness of any data or information provided through the Service.</p>
        </Section>

        <Section number={8} title="Limitation of Liability">
          <p>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NUVPORT.COM SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE.</p>
          <p style={{ marginTop: 12 }}>IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.</p>
        </Section>

        <Section number={9} title="Termination">
          <p>You may terminate your account at any time by contacting us at nuvportos@gmail.com. We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice.</p>
          <p style={{ marginTop: 12 }}>Upon termination, your right to use the Service will immediately cease. Provisions of these Terms that by their nature should survive termination shall survive, including Sections 5, 6, 7, 8, and 10.</p>
        </Section>

        <Section number={10} title="Governing Law">
          <p>These Terms shall be governed by and construed in accordance with applicable law. Any disputes arising under these Terms shall be resolved in the courts of the applicable jurisdiction.</p>
          <p style={{ marginTop: 12 }}>If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.</p>
        </Section>

        <Section number={11} title="Changes to Terms">
          <p>We reserve the right to modify these Terms at any time. We will provide notice of significant changes by updating the effective date at the top of this page and, where appropriate, by notifying you via email or in-app notification.</p>
          <p style={{ marginTop: 12 }}>Your continued use of the Service after any changes constitutes your acceptance of the new Terms. If you do not agree to the revised Terms, you must stop using the Service.</p>
        </Section>

        <Section number={12} title="Contact">
          <p>If you have any questions about these Terms, please contact us:</p>
          <p style={{ marginTop: 12, color: GREEN }}>nuvportos@gmail.com</p>
          <p style={{ marginTop: 8 }}>nuvport.com · Nuvport OS</p>
        </Section>

      </div>
    </div>
  );
}
